/**
 * Builds one `InstancedMesh` per distinct baked `objectRef` out of a
 * region's resolved placements — shared by `/fly`'s region loader
 * (`flight/fly-region.ts`) and the `/assets` World viewer's 3D pane
 * (`assets/world-scene.ts`) — plus one line mesh carrying every placement's
 * terrain edges (`polygon-outlines.ts` for why that exists at all: without it
 * the region's whole road and river network rasterizes to nothing).
 *
 * **Instancing choice (InstancedMesh over a merged BufferGeometry):** a
 * region has hundreds of placements but only a few dozen *distinct* models
 * (world-terrain.md §3 — repeated object ids form the "towns"; see this
 * lane's counts: 30-57 distinct refs per region against 246-397 resolved
 * placements), so grouping by `objectRef` is the natural geometry-sharing
 * boundary either way. `InstancedMesh` is chosen over merging every
 * instance's vertices into one static buffer because (a) it needs no CPU-side
 * per-vertex transform/duplication at load time — just one small 4x4 matrix
 * per placement, (b) three.js's WebGPU renderer instances it natively in one
 * draw call per distinct ref regardless of instance count, and (c) it keeps
 * each placement individually addressable by instance index, which the
 * World viewer's "select a placement, focus it in 3D" interaction needs —
 * a merged geometry would have to be rebuilt (or paired with a second,
 * parallel non-merged copy) to answer "where is placement N" at all.
 *
 * The line mesh goes the other way and *is* merged: its instance slot is
 * already spent on the segment, so each placement's segments are transformed
 * on the CPU into one shared buffer. That is affordable precisely because
 * there are so few of them — Verdun's whole terrain is 8,111 placed
 * triangles, i.e. ~24,000 segments.
 *
 * The -90° model-frame fix (`world/placement-transform.ts`'s
 * `modelFrameToWorld`) is baked into each shared geometry once via
 * `geometry.rotateX(...)` rather than composed into every per-instance
 * matrix — it's the same fixed rotation for every instance of one object,
 * so paying for it once per distinct ref instead of once per placement is
 * strictly cheaper, and it means the line segments read straight off the
 * already-oriented geometry.
 *
 * **The other two node kinds.** A `.TBL` LOD carries four kinds of primitive
 * (docs/formats/models-tbl.md §4.3) and this builds all four. Spheres
 * (`sphere-instances.ts`) are batched by colour across the whole region — that
 * is the tree canopies, two draw calls for the lot. Billboards are *not* drawn
 * here: they have no world size, only a screen size that changes every frame
 * with the camera (`sprite-screen-size.ts`), so a region hands its placed
 * billboards back as data and whoever owns a camera draws them through a
 * `ScreenSpritePool`. Measured, that list is **empty for every shipped
 * region**: no `.WLD` placement in any of the six references a sprite-bearing
 * object, because only `FLAK`/`EXPLO`/`SMOKE`/`MISC` contain type-2/3 nodes
 * and the placements only ever name `TERR1-5` and `PTPLANE`. It is collected
 * anyway because "which objects a region places" is data, not a code
 * assumption, and the alternative is silently dropping a primitive kind.
 *
 * **What is next door.** Loading a single ref into the geometry its placements
 * share lives in `world-shared-ref.ts`; this file is the *region* half — which
 * refs there are, where each copy goes, and how the whole lot is folded into
 * meshes, spheres and one line buffer. The split is the 400-line ceiling, taken
 * at the seam that was already there.
 */

import * as THREE from "three/webgpu";
import type { BakedModelBillboard, BakedModelBody, BakedModelSpriteSheet } from "./baked-model.ts";
import {
  instanceMatrixFor,
  loadSharedRef,
  type InstancerPlacement,
  type SharedRef,
} from "./world-shared-ref.ts";
import { liftHairlinesOffGround, plateCandidateFor, separatePlatesByPaintOrder } from "./plate-paint-order.ts";
import { createHairlines, type Hairlines } from "./hairline-mesh.ts";
import { buildSphereMeshes, worldSpheres, type SpherePlacement, type WorldSphere } from "./sphere-instances.ts";
import { buildSegmentBuffers, ORIGINAL_FRAMEBUFFER_WIDTH_PX, type SegmentBatch } from "./polygon-outlines.ts";
import { modelFrameToWorld, placementWorldTransform, ORIGIN_ZERO, type PlacementOrigin } from "../world/placement-transform.ts";
import {
  drapeGeometry,
  drapeSegments,
  wantsVertexDrape,
  type GroundSampler,
} from "./terrain-drape.ts";
import type { GroundLattice } from "./lattice-clip.ts";
import {
  applyLandcoverShading,
  shadedPlateFor,
  woodGroundPaintCensus,
  type ShadedPlate,
  type WoodGroundPaintCensus,
} from "./landcover-plate-colours.ts";
import type { TreeSite } from "./wood-siting.ts";
import { structureSwap, type StructureSwap } from "./enhanced-structure.ts";
import { oceanSwap, type OceanSource, type OceanSwap } from "./ocean-plates.ts";

export type { InstancerPlacement };

export interface WorldInstancerOptions {
  /**
   * The view's width in **CSS pixels** (`renderer.getSize()`, never
   * `getDrawingBufferSize()` — see `polygon-outlines.ts`'s `originalPixelWidth`),
   * for the terrain lines' one-original-pixel width. Defaults to the original's
   * own 320, i.e. one CSS pixel — correct only if the caller really is
   * presenting 320 across.
   */
  readonly viewWidthCssPx?: number;
  /**
   * Where a thing placed at the datum belongs (`three/relief-ground.ts`'s
   * **`drapeAt`**, not its `sampleAt` — the difference is the datum clearance,
   * and spending it was a z-fighting bug). Given one, every placed thing is put
   * back on the ground (`terrain-drape.ts`); omitted, everything stays at the
   * flat datum, which is the pre-relief behaviour and what a caller with no
   * region wants.
   */
  readonly ground?: GroundSampler;
  /** The ground mesh's own lattice, so plates and hairlines are cut coplanar with it. Required whenever `ground` is given. */
  readonly groundLattice?: GroundLattice;
  /**
   * ENHANCED TERRAIN's field hillshading (`world/landcover-shading.ts`). Given
   * one, every draped plate gets a *second* colour buffer shaded by its own
   * slope, and `setLandcoverShading` swaps between them; omitted, the plates
   * keep their single authored tone, which is the toggle's "off is today's
   * pixels" contract.
   */
  readonly landcoverShade?: (tone: number, normalX: number, normalY: number, normalZ: number) => number;
  /**
   * The hour's walk down the palette (`flight/time-of-day.ts`'s
   * `groundRampShiftFor`). Applied to every placed face whose colour is on a
   * run that can be walked — see `world/terrain-palette.ts`'s `DIMMABLE`.
   */
  readonly rampShift?: number;
  /**
   * Bodies already in hand, keyed by `objectRef`. `flight/fly-region.ts` reads
   * the whole set to derive the region's relief before any mesh is built, so
   * handing them over here is what stops every object being fetched twice. A
   * ref that is missing is fetched as usual, which is what every other caller
   * relies on.
   */
  readonly models?: ReadonlyMap<string, BakedModelBody>;
  /**
   * The region's derived wood sites (`world/terrain-features.ts`), for G20's
   * canopy ground paint. Absent means no paint, which is what the `/assets`
   * viewer and the plane-viewer aerodrome want; and the paint needs
   * `landcoverShade` too, since it rides the shaded buffer the same toggle
   * swaps in (`three/wood-ground-paint.ts`).
   */
  readonly woodSites?: readonly TreeSite[];
  /** ENHANCED MODELS for the buildings, as the enhanced index (`three/enhanced-structure.ts`); omitted for the authentic world. */
  readonly enhancedModels?: ReadonlyMap<string, { readonly path: string }>;
  /** ENHANCED TERRAIN's sea (`three/ocean-plates.ts`); omitted — every caller but `/fly` — the `s = 10` plate stays the flat blue quad it has always been. */
  readonly ocean?: OceanSource;
}

/**
 * One placed sprite node, resolved to a world anchor and the sheet its frame
 * lives in — everything a `ScreenSpritePool` needs except the camera.
 */
export interface WorldBillboard {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly sheet: BakedModelSpriteSheet;
  readonly billboard: BakedModelBillboard;
}

export interface WorldInstances {
  readonly group: THREE.Group;
  /** One `InstancedMesh` per distinct resolved `objectRef` that loaded successfully. */
  readonly meshes: ReadonlyMap<string, THREE.InstancedMesh>;
  /** One `InstancedMesh` per distinct sphere colour in the whole region (`sphere-instances.ts`). */
  readonly sphereMeshes: readonly THREE.InstancedMesh[];
  /** Every placed sprite node, for a caller that owns a camera to draw. Empty for all six shipped regions — see the module doc comment. */
  readonly billboards: readonly WorldBillboard[];
  /** Draw calls this scenery adds (one per entry in `meshes`, one per sphere colour, plus one for the terrain lines) — the authentic count, with the canopy discs shown. */
  readonly drawCallCount: number;
  /**
   * Shows or hides the tree canopy discs.
   *
   * The *only* caller is ENHANCED TERRAIN (`flight/scene-terrain.ts`), which
   * hides them because its own foliage stands in the same places
   * (`three/terrain-decoration.ts`) and drawing both is drawing each tree
   * twice. Default is shown, and with the setting off nothing ever calls this,
   * so the authentic path is bit-for-bit what it was.
   *
   * Hiding *every* sphere in the region is exactly right and not a shortcut:
   * AD-019's census over all six shipped worlds found the tree object is the
   * only sphere-bearing thing any `.WLD` places, and `world/terrain-features.ts`
   * derives a copse from every one of those placements — so the set hidden here
   * and the set replaced there are the same set by construction.
   */
  readonly setCanopyDiscsVisible: (visible: boolean) => void;
  /** Refs present in `placements` whose model failed to load — skipped, not fatal (matches the prior hand-placed scenery's "skip what fails to load" precedent). */
  readonly failedRefs: readonly string[];
  /** How many terrain line segments the region drew — reported for the HUD/debug the same way `drawCallCount` is. */
  readonly lineSegmentCount: number;
  /** Keeps the terrain lines one original pixel wide across a window resize — or a pixel-ratio change (`three/view-pixel-ratio.ts`). CSS pixels. */
  readonly setViewWidth: (viewWidthCssPx: number) => void;
  /** Swaps the draped plates between their authored tone and the slope-shaded one. A no-op without `landcoverShade`. */
  readonly setLandcoverShading: (enabled: boolean) => void;
  /** ENHANCED MODELS for the buildings: the swap, and the census that says whether it has anything to swap. */
  readonly enhancedStructures: StructureSwap;
  /** ENHANCED TERRAIN's sea: the same swap for the region's `s = 10` plate, plus the per-frame clock (`three/ocean-plates.ts`). */
  readonly ocean: OceanSwap;
  /** How many plates carry a shaded buffer — 0 means the toggle has nothing to show, which is worth reporting rather than guessing at. */
  readonly shadedPlateCount: number;
  /** G20's census: what the canopy ground paint actually covers in this region (`three/wood-ground-paint.ts`). */
  readonly woodGroundPaint: WoodGroundPaintCensus;
}

function groupByRef(placements: readonly InstancerPlacement[]): Map<string, InstancerPlacement[]> {
  const byRef = new Map<string, InstancerPlacement[]>();
  for (const placement of placements) {
    if (placement.objectRef === null) continue;
    const list = byRef.get(placement.objectRef);
    if (list) list.push(placement);
    else byRef.set(placement.objectRef, [placement]);
  }
  return byRef;
}

/** What one ref's placements contribute to the region, before any of it becomes a three.js object. */
interface RegionAccumulator {
  readonly meshes: Map<string, THREE.InstancedMesh>;
  readonly failedRefs: string[];
  /**
   * One slot per distinct ref, indexed by that ref's position in `.WLD` record
   * order — **not** appended as each ref finishes. The refs load concurrently,
   * so append order is fetch-completion order, which differs from run to run;
   * and `buildSegmentBuffers` resolves coincident terrain edges by paint order,
   * so a shuffled batch list would hand a field boundary a different colour on
   * every load. Sparse where a ref contributed no segments; compacted below.
   */
  readonly batches: (SegmentBatch | undefined)[];
  readonly spheres: WorldSphere[];
  /** Draped plates that have a slope-shaded alternative, for `setLandcoverShading` to swap in (`landcover-plate-colours.ts`). */
  readonly shadedPlates: ShadedPlate[];
  readonly billboards: WorldBillboard[];
}

/** Every copy of one ref's sprite nodes, anchored in world metres — the placement's yaw and translation applied to each node's own model-space anchor. */
function placedBillboards(shared: SharedRef, transforms: readonly SpherePlacement[]): WorldBillboard[] {
  const sheet = shared.spriteSheet;
  if (!sheet || shared.billboards.length === 0) return [];
  const out: WorldBillboard[] = [];
  for (const placement of transforms) {
    const cos = Math.cos(placement.yawRadians);
    const sin = Math.sin(placement.yawRadians);
    for (const billboard of shared.billboards) {
      const local = modelFrameToWorld(billboard.position[0], billboard.position[1], billboard.position[2]);
      out.push({
        x: cos * local.x + sin * local.z + placement.x,
        y: local.y + placement.y,
        z: -sin * local.x + cos * local.z + placement.z,
        sheet,
        billboard,
      });
    }
  }
  return out;
}

/** Folds one distinct `objectRef` and all of its placements into `into`. */
function collectRef(
  into: RegionAccumulator,
  batchSlot: number,
  objectRef: string,
  refPlacements: readonly InstancerPlacement[],
  origin: PlacementOrigin,
  material: THREE.Material,
  ground: GroundSampler | undefined,
  groundLattice: GroundLattice | undefined,
  landcoverShade: WorldInstancerOptions["landcoverShade"],
  woodSites: readonly TreeSite[],
  rampShift: number,
  shared: SharedRef | undefined,
): THREE.InstancedMesh | undefined {
  if (!shared) {
    into.failedRefs.push(objectRef);
    return undefined;
  }

  // A region-scale plate is placed once and its geometry already *is* world
  // geometry, so it drapes per vertex; everything else rides its instance.
  const vertexDraped =
    ground !== undefined && groundLattice !== undefined && wantsVertexDrape(shared.boundingRadius, refPlacements.length);
  if (vertexDraped && ground && groundLattice) {
    const only = placementWorldTransform(refPlacements[0]!, origin);
    drapeGeometry(shared.geometry, ground, groundLattice, only.position.x, only.position.z);
    // Only after the drape, for both halves: the slope a field is shaded by,
    // and the footprint "is this face under a wood" is asked of, are both
    // properties of the cut geometry (`landcover-plate-colours.ts`).
    const plate = shadedPlateFor(
      shared.geometry,
      only.position.x,
      only.position.z,
      landcoverShade,
      woodSites,
      rampShift,
    );
    if (plate) into.shadedPlates.push(plate);
  }

  const transforms = refPlacements.map((placement) => {
    const transform = placementWorldTransform(placement, origin);
    const lift = ground && !vertexDraped ? ground(transform.position.x, transform.position.z) : 0;
    return {
      yawRadians: transform.yawRadians,
      x: transform.position.x,
      y: transform.position.y + lift,
      z: transform.position.z,
    };
  });
  if (shared.segments.segmentCount > 0) into.batches[batchSlot] = { source: shared.segments, placements: transforms };
  into.spheres.push(...worldSpheres(shared.spheres, transforms));
  into.billboards.push(...placedBillboards(shared, transforms));

  if (shared.geometry.getAttribute("position") === undefined) return undefined;
  const mesh = new THREE.InstancedMesh(shared.geometry, material, refPlacements.length);
  refPlacements.forEach((placement, index) =>
    mesh.setMatrixAt(index, instanceMatrixFor(placement, origin, vertexDraped ? undefined : ground)),
  );
  mesh.instanceMatrix.needsUpdate = true;
  mesh.computeBoundingSphere();
  into.meshes.set(objectRef, mesh);
  return mesh;
}

/**
 * Builds the instanced scenery group for `placements`, recentred so that
 * `origin` (world metres) lands at the local origin — callers pick what
 * `origin` means: the chosen start aerodrome for `/fly`, or a region's own
 * bounds centre for an overview pane. One shared `MeshBasicMaterial`
 * (unlit, vertex-coloured — matches every other baked-model render path in
 * this app) is reused across every instanced mesh this builds, spheres
 * included.
 */
export async function buildWorldInstances(
  placements: readonly InstancerPlacement[],
  origin: PlacementOrigin = ORIGIN_ZERO,
  options: WorldInstancerOptions = {},
): Promise<WorldInstances> {
  const group = new THREE.Group();
  // Absent for a region with no relief, which is what stops a coastal map
  // cutting its 378 km sea plate against ground that is not there. Both halves
  // come from the same `ReliefGround` and neither is usable alone, so one
  // missing means no drape.
  const ground = options.groundLattice ? options.ground : undefined;
  const rampShift = options.rampShift ?? 0;
  const material = new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide });
  const into: RegionAccumulator = {
    meshes: new Map(),
    failedRefs: [],
    batches: [],
    spheres: [],
    billboards: [],
    shadedPlates: [],
  };

  // `groupByRef` is a `Map`, so its entries come out in first-appearance order
  // — i.e. `.WLD` record order, which is the order the original paints in.
  const entries = [...groupByRef(placements).entries()];
  const loaded = await Promise.all(
    entries.map(([ref]) => loadSharedRef(ref, options.models?.get(ref), rampShift, options.enhancedModels?.get(ref)?.path).catch(() => undefined)),
  );
  // Every ref is in hand before any of them is draped, because the plates have
  // to be separated *from each other* first: several of them cover the same
  // ground at the same height, `decal-layers.ts` cannot see across two models,
  // and the depth buffer then lets the camera pick the colour
  // (`plate-paint-order.ts` — the 2026-08-26 "ground switches from brown to
  // green" report). Record order is paint order, which is why the load is
  // gathered in `entries` order rather than completion order.
  separatePlatesByPaintOrder(entries.map(([, refPlacements], slot) => plateCandidateFor(loaded[slot], refPlacements, origin)));
  entries.forEach(([objectRef, refPlacements], slot) => {
    const mesh = collectRef(
      into,
      slot,
      objectRef,
      refPlacements,
      origin,
      material,
      ground,
      options.groundLattice,
      options.landcoverShade,
      options.woodSites ?? [],
      rampShift,
      loaded[slot],
    );
    if (mesh) group.add(mesh);
  });

  const enhancedStructures = structureSwap(entries, loaded, into.meshes);
  // After the walk rather than inside it: which refs are sea is a property of
  // the loaded bodies, and the meshes are already keyed by ref.
  const ocean = oceanSwap(entries, loaded, into.meshes, material, options.ocean);

  const sphereMeshes = buildSphereMeshes(into.spheres, material);
  for (const mesh of sphereMeshes) group.add(mesh);

  // Sparse where a ref contributed no segments — compacted here, keeping the
  //  record order main established for deterministic paint order.
  const merged = buildSegmentBuffers(into.batches.filter((batch) => batch !== undefined));
  // The endpoints are already world space, so the road and river network follows
  // the ground it was derived from — cut on the mesh's cells first, or a
  // multi-kilometre segment chords straight through the hills it crosses.
  const segmentBuffers =
    ground && options.groundLattice ? drapeSegments(merged, ground, options.groundLattice) : merged;
  // A hairline's endpoints were read off the plate under it and draped through
  // the same sampler, so the two are the *same float* and the depth buffer has
  // nothing to arbitrate — B3's "roads are z-fighting on the ground".
  // `plate-paint-order.ts` carries the measurement and why the clearance is
  // half a plate step.
  liftHairlinesOffGround(segmentBuffers.positions);
  const hairlines: Hairlines | undefined = createHairlines(
    segmentBuffers,
    options.viewWidthCssPx ?? ORIGINAL_FRAMEBUFFER_WIDTH_PX,
  );
  if (hairlines) group.add(hairlines.mesh);

  return {
    group,
    meshes: into.meshes,
    sphereMeshes,
    billboards: into.billboards,
    drawCallCount: into.meshes.size + sphereMeshes.length + (hairlines ? 1 : 0),
    failedRefs: into.failedRefs,
    lineSegmentCount: (hairlines?.mesh.geometry as THREE.InstancedBufferGeometry | undefined)?.instanceCount ?? 0,
    setViewWidth: (widthCssPx: number) => hairlines?.setViewWidth(widthCssPx),
    setCanopyDiscsVisible: (visible: boolean) => {
      for (const mesh of sphereMeshes) mesh.visible = visible;
    },
    shadedPlateCount: into.shadedPlates.length,
    woodGroundPaint: woodGroundPaintCensus(into.shadedPlates, options.woodSites ?? []),
    setLandcoverShading: (enabled: boolean) => applyLandcoverShading(into.shadedPlates, enabled),
    enhancedStructures,
    ocean,
  };
}
